
import { useState } from 'react';
import { Category, Assessment } from '@/types/assessment';

export interface AssessmentEditState {
  loading: boolean;
  title: string;
  description: string;
  slug: string;
  categories: Category[];
  isActive: boolean;
  isMaturityModel: boolean;
  notifyEmails: string;
  calendlyLink: string;
  assessment: Assessment | null;
  confirmingVersionChange: boolean;
}

export function useAssessmentState() {
  const [state, setState] = useState<AssessmentEditState>({
    loading: false,
    title: '',
    description: '',
    slug: '',
    categories: [],
    isActive: true,
    isMaturityModel: false,
    notifyEmails: '',
    calendlyLink: '',
    assessment: null,
    confirmingVersionChange: false,
  });

  // Individual setters for state properties
  const setLoading = (loading: boolean) => setState(prev => ({ ...prev, loading }));
  const setTitle = (title: string) => setState(prev => ({ ...prev, title }));
  const setDescription = (description: string) => setState(prev => ({ ...prev, description }));
  const setSlug = (slug: string) => setState(prev => ({ ...prev, slug }));
  const setCategories = (categories: Category[] | ((prev: Category[]) => Category[])) => {
    if (typeof categories === 'function') {
      setState(prev => ({ ...prev, categories: categories(prev.categories) }));
    } else {
      setState(prev => ({ ...prev, categories }));
    }
  };
  const setIsActive = (isActive: boolean) => setState(prev => ({ ...prev, isActive }));
  const setIsMaturityModel = (isMaturityModel: boolean) => setState(prev => ({ ...prev, isMaturityModel }));
  const setNotifyEmails = (notifyEmails: string) => setState(prev => ({ ...prev, notifyEmails }));
  const setCalendlyLink = (calendlyLink: string) => setState(prev => ({ ...prev, calendlyLink }));
  const setAssessment = (assessment: Assessment | null) => setState(prev => ({ ...prev, assessment }));
  const setConfirmingVersionChange = (confirmingVersionChange: boolean) => 
    setState(prev => ({ ...prev, confirmingVersionChange }));

  // Function to populate assessment data from API response
  const populateAssessmentData = (assessment: Assessment) => {
    setTitle(assessment.title);
    setDescription(assessment.description);
    setSlug(assessment.slug);
    setCategories(assessment.categories);
    setIsActive(assessment.isActive);
    setIsMaturityModel(assessment.isMaturityModel || false);
    setNotifyEmails(assessment.notifyEmails || '');
    setCalendlyLink(assessment.calendlyLink || '');
  };

  return {
    state,
    setLoading,
    setTitle,
    setDescription,
    setSlug,
    setCategories,
    setIsActive,
    setIsMaturityModel,
    setNotifyEmails,
    setCalendlyLink,
    setAssessment,
    setConfirmingVersionChange,
    populateAssessmentData,
  };
}
